import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { X, MapPin, Phone, Calendar, Clock, Grid, Tag, Download } from 'lucide-react';
import 'mapbox-gl/dist/mapbox-gl.css';
import ProjectPDFSnapshot from './ProjectPDFSnapshot';
import PhotoViewer from './PhotoViewer';

// Set Mapbox token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

const ProjectDetails = ({ project, onClose }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [showPDFExport, setShowPDFExport] = useState(false);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const handleResizeRef = useRef(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Add debugging to see what project data we have
  useEffect(() => {
    // Project data is now working correctly
  }, [project]);

  useEffect(() => {
    if (!mapContainer.current || !mapboxgl.accessToken) {
      setMapError('Map initialization failed. Please check your Mapbox configuration.');
      return;
    }

    try {
      console.log('Initializing map with project:', project);
      console.log('Map container:', mapContainer.current);
      console.log('Mapbox token:', mapboxgl.accessToken ? 'Present' : 'Missing');
      
      // Initialize map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: project.mapCenter || [-122.4194, 37.7749],
        zoom: project.mapZoom || 13,
        interactive: true // Enable interactions for better user experience
      });

      console.log('Map created successfully');

      // Add navigation control
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Load map data when style is loaded
      map.current.on('style.load', () => {
        console.log('Map style loaded');
        // Add a small delay to ensure the map is fully rendered
        setTimeout(() => {
          try {
            // First, reconstruct conductors to get proper coordinate format
            let reconstructedConductors = [];
            if (project.conductors?.length > 0) {
              console.log('Reconstructing conductors for bounds calculation...');
              reconstructedConductors = project.conductors.map((conductor, index) => {
                // Reconstruct coordinates from flattened format
                let reconstructedCoordinates = [];
                if (conductor.coordinates && Array.isArray(conductor.coordinates)) {
                  // Check if coordinates are in flattened format (objects with lng/lat) or array format
                  if (conductor.coordinates.length > 0 && typeof conductor.coordinates[0] === 'object' && 'lng' in conductor.coordinates[0]) {
                    // Flattened format: convert back to array format
                    reconstructedCoordinates = conductor.coordinates.map(coord => [coord.lng, coord.lat]);
                  } else {
                    // Already in array format (for backward compatibility)
                    reconstructedCoordinates = conductor.coordinates;
                  }
                } else if (conductor.coordinates && typeof conductor.coordinates === 'string') {
                  // Handle case where coordinates might be serialized as string
                  try {
                    const parsed = JSON.parse(conductor.coordinates);
                    if (Array.isArray(parsed)) {
                      reconstructedCoordinates = parsed;
                    }
                  } catch (parseError) {
                    console.warn('Failed to parse coordinates string:', parseError);
                  }
                }
                
                return {
                  ...conductor,
                  coordinates: reconstructedCoordinates
                };
              });
            }

            // Collect all coordinates for bounds calculation
            const bounds = new mapboxgl.LngLatBounds();
            let hasValidBounds = false;

            // Add equipment coordinates to bounds
            project.equipment?.forEach(item => {
              if (item.lngLat && Array.isArray(item.lngLat) && item.lngLat.length === 2 && 
                  !isNaN(item.lngLat[0]) && !isNaN(item.lngLat[1]) &&
                  item.lngLat[0] !== null && item.lngLat[1] !== null &&
                  item.lngLat[0] !== undefined && item.lngLat[1] !== undefined) {
                try {
                  bounds.extend(item.lngLat);
                  hasValidBounds = true;
                  console.log('Added equipment to bounds:', item.type, item.lngLat);
                } catch (error) {
                  console.warn('Invalid equipment coordinates:', item.lngLat);
                }
              }
            });

            // Add reconstructed conductor coordinates to bounds
            reconstructedConductors.forEach(conductor => {
              if (conductor.coordinates) {
                conductor.coordinates.forEach(coord => {
                  if (Array.isArray(coord) && coord.length === 2 && 
                      !isNaN(coord[0]) && !isNaN(coord[1]) &&
                      coord[0] !== null && coord[1] !== null &&
                      coord[0] !== undefined && coord[1] !== undefined) {
                    try {
                      bounds.extend(coord);
                      hasValidBounds = true;
                      console.log('Added conductor coordinate to bounds:', coord);
                    } catch (error) {
                      console.warn('Invalid conductor coordinates:', coord);
                    }
                  }
                });
              }
            });

            // Fit bounds to equipment/conductors if available, otherwise use viewport
            if (hasValidBounds) {
              console.log('Fitting bounds to equipment/conductors:', bounds);
              try {
                map.current.fitBounds(bounds, {
                  padding: { top: 60, bottom: 60, left: 60, right: 60 },
                  duration: 1000 // Add smooth animation
                });
              } catch (boundsError) {
                console.warn('Error fitting bounds:', boundsError);
                // Fallback to default center
                map.current.setCenter(project.mapCenter || [-122.4194, 37.7749]);
                map.current.setZoom(project.mapZoom || 13);
              }
            } else if (project.viewport?.bounds) {
              // Fallback to stored viewport if no equipment/conductors
              console.log('Using stored viewport bounds:', project.viewport.bounds);
              try {
                map.current.fitBounds(project.viewport.bounds, {
                  padding: project.viewport.padding,
                  duration: 1000
                });
              } catch (viewportError) {
                console.warn('Error fitting viewport bounds:', viewportError);
                // Fallback to default center
                map.current.setCenter(project.mapCenter || [-122.4194, 37.7749]);
                map.current.setZoom(project.mapZoom || 13);
              }
            } else {
              // Final fallback - ensure map is visible
              console.log('No bounds available, using default center');
              map.current.setCenter(project.mapCenter || [-122.4194, 37.7749]);
              map.current.setZoom(project.mapZoom || 13);
            }

            // Add equipment markers
            project.equipment?.forEach(item => {
              if (item.lngLat && Array.isArray(item.lngLat) && item.lngLat.length === 2 && 
                  !isNaN(item.lngLat[0]) && !isNaN(item.lngLat[1]) &&
                  item.lngLat[0] !== null && item.lngLat[1] !== null &&
                  item.lngLat[0] !== undefined && item.lngLat[1] !== undefined) {
                
                try {
                  console.log('Creating marker for:', item.type, item.lngLat);
                  const markerElement = createEquipmentMarker(item);

                  new mapboxgl.Marker(markerElement)
                    .setLngLat(item.lngLat)
                    .setPopup(
                      new mapboxgl.Popup({ offset: 25 })
                        .setHTML(`
                          <div style="padding: 8px;">
                            <h3 style="font-weight: 600; margin-bottom: 4px;">${formatEquipmentType(item.type)}</h3>
                            ${item.notes ? `<p style="margin: 4px 0; color: #666;">${item.notes}</p>` : ''}
                            ${Object.entries(item.details || {})
                              .map(([key, value]) => `<p style="margin: 2px 0;"><strong>${key}:</strong> ${value}</p>`)
                              .join('')}
                          </div>
                        `)
                    )
                    .addTo(map.current);
                } catch (error) {
                  console.warn('Error creating equipment marker:', error, item);
                }
              }
            });

            // Add conductors if they exist and have valid coordinates
            console.log('=== PROJECT DETAILS CONDUCTOR DEBUG ===');
            console.log('Project conductors:', project.conductors);
            console.log('Project conductors type:', typeof project.conductors);
            console.log('Project conductors length:', project.conductors?.length);
            
            if (project.conductors?.length > 0) {
              console.log('Processing conductors in ProjectDetails...');
              
              // Use the already reconstructed conductors
              const validConductors = reconstructedConductors.filter(conductor => {
                console.log('Checking reconstructed conductor:', conductor);
                console.log('Reconstructed conductor coordinates:', conductor.coordinates);
                console.log('Reconstructed conductor coordinates type:', typeof conductor.coordinates);
                console.log('Reconstructed conductor coordinates length:', conductor.coordinates?.length);
                
                const isValid = conductor.coordinates?.length >= 2 && 
                  conductor.coordinates.every(coord => 
                    Array.isArray(coord) && coord.length === 2 && 
                    !isNaN(coord[0]) && !isNaN(coord[1])
                  );
                
                console.log('Conductor is valid:', isValid);
                return isValid;
              });

              console.log('Valid conductors count:', validConductors.length);
              console.log('Valid conductors:', validConductors);

              if (validConductors.length > 0) {
                try {
                  console.log('Adding conductor source to map...');
                  map.current.addSource('conductors', {
                    type: 'geojson',
                    data: {
                      type: 'FeatureCollection',
                      features: validConductors.map(conductor => {
                        console.log('Creating feature for conductor:', conductor.type, 'with coordinates:', conductor.coordinates);
                        return {
                          type: 'Feature',
                          geometry: {
                            type: 'LineString',
                            coordinates: conductor.coordinates
                          },
                          properties: {
                            type: conductor.type
                          }
                        };
                      })
                    }
                  });

                  // Add conductor layers with different styles
                  const conductorStyles = {
                    'overhead-conductor': {
                      color: '#2563eb',
                      width: 2,
                      dasharray: [2, 2]
                    },
                    'underground-conductor': {
                      color: '#dc2626',
                      width: 2,
                      dasharray: [2, 2]
                    },
                    'overhead-service': {
                      color: '#2563eb',
                      width: 1,
                      dasharray: [1, 1]
                    },
                    'underground-service': {
                      color: '#dc2626',
                      width: 1,
                      dasharray: [1, 1]
                    }
                  };

                  Object.entries(conductorStyles).forEach(([type, style]) => {
                    try {
                      console.log(`Adding conductor layer: ${type}`);
                      map.current.addLayer({
                        id: `conductors-${type}`,
                        type: 'line',
                        source: 'conductors',
                        layout: {},
                        paint: {
                          'line-color': style.color,
                          'line-width': style.width,
                          'line-dasharray': style.dasharray
                        },
                        filter: ['==', ['get', 'type'], type]
                      });
                    } catch (layerError) {
                      console.warn('Error adding conductor layer:', layerError);
                    }
                  });

                  // Add conductor labels
                  validConductors.forEach((conductor, index) => {
                    try {
                      console.log(`Adding conductor label ${index + 1} for conductor:`, conductor);
                      const coordinates = conductor.coordinates;
                      const start = coordinates[0];
                      const end = coordinates[1];
                      
                      // Calculate midpoint for label placement
                      const midpoint = [
                        (start[0] + end[0]) / 2,
                        (start[1] + end[1]) / 2
                      ];

                      // Create label container
                      const labelContainer = document.createElement('div');
                      labelContainer.className = 'conductor-label-container';

                      // Create label element
                      const label = document.createElement('div');
                      label.className = 'conductor-label';
                      const length = parseFloat(conductor.length);
                      label.innerHTML = `${conductor.wireSize}<br>${isNaN(length) ? '0.0' : length.toFixed(1)} ft`;

                      // Add label to container
                      labelContainer.appendChild(label);

                      // Add marker with label
                      new mapboxgl.Marker({
                        element: labelContainer,
                        anchor: 'center',
                        offset: [0, -2]
                      })
                        .setLngLat(midpoint)
                        .addTo(map.current);

                      // Calculate angle for label rotation
                      const angle = Math.atan2(end[1] - start[1], end[0] - start[0]) * 180 / Math.PI;
                      let textAngle = angle;
                      if (angle > 90 || angle < -90) {
                        textAngle += 180;
                      }
                      label.style.transform = `rotate(${textAngle}deg)`;
                      
                      console.log(`Conductor label ${index + 1} added at midpoint:`, midpoint);
                    } catch (labelError) {
                      console.warn('Error creating conductor label:', labelError);
                    }
                  });
                  
                  console.log('Conductors added successfully to ProjectDetails map');
                } catch (conductorError) {
                  console.warn('Error adding conductors to map:', conductorError);
                }
              } else {
                console.log('No valid conductors found');
              }
            } else {
              console.log('No conductors found in project data');
            }
            console.log('=== END PROJECT DETAILS CONDUCTOR DEBUG ===');
          } catch (error) {
            console.error('Error setting up map features:', error);
            setMapError('Error loading map features. Please try again.');
          }
        }, 100); // Small delay to ensure map is ready
      });

      // Also handle the 'load' event as a backup
      map.current.on('load', () => {
        console.log('Map loaded successfully');
      });

      // Handle resize events
      const handleResize = () => {
        if (map.current) {
          try {
            map.current.resize();
          } catch (resizeError) {
            console.warn('Error resizing map:', resizeError);
          }
        }
      };

      // Store the function in ref for cleanup
      handleResizeRef.current = handleResize;

      window.addEventListener('resize', handleResize);

      map.current.on('error', (error) => {
        console.error('Map error:', error);
        setMapError('Error loading map. Please check your internet connection.');
      });

    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError('Error initializing map. Please try again.');
    }

    return () => {
      if (handleResizeRef.current) {
        window.removeEventListener('resize', handleResizeRef.current);
      }
      if (map.current) {
        try {
          map.current.remove();
        } catch (removeError) {
          console.warn('Error removing map:', removeError);
        }
      }
    };
  }, [project]);

  const getEquipmentColor = (type) => {
    const colors = {
      'existing-pole': '#6b7280', // lighter gray
      'pole': '#4b5563', // gray
      'transformer': '#059669', // green
      'switch': '#7c3aed', // purple
      'fuse': '#ea580c', // orange
      'anchor': '#dc2626', // red
      'terminating-cabinet': '#0891b2', // cyan
      'secondary-pedestal': '#059669', // green
      'meter': '#f59e0b', // amber
      'general-note': '#8b5cf6', // violet
      'tree-trimming': '#16a34a', // green
      'default': '#2563eb' // blue
    };
    return colors[type] || colors.default;
  };

  const formatEquipmentType = (type) => {
    return type.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const createEquipmentMarker = (item) => {
    const markerElement = document.createElement('div');
    markerElement.className = `equipment-marker ${item.type}`;
    markerElement.style.width = '24px';
    markerElement.style.height = '24px';
    markerElement.style.display = 'flex';
    markerElement.style.alignItems = 'center';
    markerElement.style.justifyContent = 'center';
    markerElement.style.backgroundColor = 'transparent';
    markerElement.style.border = 'none';
    markerElement.style.borderRadius = '0';
    markerElement.style.boxShadow = 'none';

    // Create SVG icon based on equipment type
    const svgContent = getEquipmentSVG(item.type);
    markerElement.innerHTML = svgContent;

    return markerElement;
  };

  const getEquipmentSVG = (type) => {
    const color = getEquipmentColor(type);
    
    switch (type) {
      case 'existing-pole':
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="8" stroke="${color}" strokeWidth="2" fill="none"/>
        </svg>`;
      
      case 'pole':
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line x1="6" y1="6" x2="18" y2="18" stroke="${color}" strokeWidth="2"/>
          <line x1="18" y1="6" x2="6" y2="18" stroke="${color}" strokeWidth="2"/>
        </svg>`;
      
      case 'transformer':
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="9" y="6" width="6" height="12" stroke="${color}" strokeWidth="2" fill="none"/>
        </svg>`;
      
      case 'anchor':
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line x1="12" y1="4" x2="12" y2="20" stroke="${color}" strokeWidth="2"/>
          <line x1="4" y1="4" x2="20" y2="4" stroke="${color}" strokeWidth="2"/>
        </svg>`;
      
      case 'switch':
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 12C6 9.79086 7.79086 8 10 8C12.2091 8 14 9.79086 14 12C14 14.2091 12.2091 16 10 16C7.79086 16 6 14.2091 6 12Z" stroke="${color}" strokeWidth="2" fill="none"/>
          <path d="M10 12C10 9.79086 11.7909 8 14 8C16.2091 8 18 9.79086 18 12C18 14.2091 16.2091 16 14 16C11.7909 16 10 14.2091 10 12Z" stroke="${color}" strokeWidth="2" fill="none"/>
        </svg>`;
      
      case 'terminating-cabinet':
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="6" width="16" height="12" stroke="${color}" strokeWidth="2" fill="none"/>
          <text x="12" y="14" textAnchor="middle" dominantBaseline="middle" fill="${color}" fontSize="8" fontFamily="Arial, sans-serif">TC</text>
        </svg>`;
      
      case 'secondary-pedestal':
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <polygon points="12,3 17,5 20,10 20,14 17,19 12,21 7,19 4,14 4,10 7,5" stroke="${color}" strokeWidth="2" fill="none"/>
          <text x="12" y="14" textAnchor="middle" dominantBaseline="middle" fill="${color}" fontSize="8" fontFamily="Arial, sans-serif" fontWeight="bold">SP</text>
        </svg>`;
      
      case 'meter':
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="8" stroke="${color}" strokeWidth="2" fill="none"/>
          <text x="12" y="15" textAnchor="middle" dominantBaseline="middle" fill="${color}" fontSize="10" fontFamily="Arial, sans-serif" fontWeight="bold">M</text>
        </svg>`;
      
      case 'general-note':
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="6" y="4" width="12" height="16" stroke="${color}" strokeWidth="2" fill="none" rx="1"/>
          <line x1="9" y1="8" x2="15" y2="8" stroke="${color}" strokeWidth="2"/>
          <line x1="9" y1="11" x2="15" y2="11" stroke="${color}" strokeWidth="2"/>
          <line x1="9" y1="14" x2="13" y2="14" stroke="${color}" strokeWidth="2"/>
        </svg>`;
      
      case 'tree-trimming':
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="8" r="3" fill="${color}"/>
          <circle cx="8" cy="12" r="3" fill="${color}"/>
          <circle cx="16" cy="12" r="3" fill="${color}"/>
          <rect x="11" y="15" width="2" height="6" fill="${color}"/>
        </svg>`;
      
      default:
        return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="8" stroke="${color}" strokeWidth="2" fill="none"/>
        </svg>`;
    }
  };

  return (
    <div className="fixed inset-0 bg-[var(--bg-primary)] z-50 flex flex-col">
      {/* Header */}
      <div className="bg-[var(--bg-secondary)] border-b border-[var(--border-color)] px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">{project.name}</h1>
            <p className="text-[var(--text-secondary)] mt-1">{project.customer}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPDFExport(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-secondary)] transition-colors"
            >
              <Download size={16} />
              Export PDF
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-secondary)] transition-colors border border-[var(--border-color)]"
            >
              <X size={16} />
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden relative">
        {/* Map Section */}
        <div className="flex-1 p-6 min-w-0 flex flex-col relative z-10 lg:z-0">
          <div className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] shadow-sm flex-1 flex flex-col">
            <div className="p-4 border-b border-[var(--border-color)] flex-shrink-0">
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">Project Map</h2>
            </div>
            <div 
              ref={mapContainer} 
              className="flex-1 min-h-[300px] lg:min-h-0 rounded-b-lg overflow-hidden bg-[var(--bg-tertiary)] relative"
            >
              {mapError && (
                <div className="absolute inset-0 flex items-center justify-center bg-red-900/20 text-red-400 border border-red-500/30 rounded-lg m-4">
                  {mapError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Details Section */}
        <div className="w-full lg:w-96 bg-[var(--bg-secondary)] p-6 overflow-y-auto border-l border-[var(--border-color)] relative z-0 max-h-[40vh] lg:max-h-none lg:z-10">
          <div className="space-y-6">
            {/* Project Info */}
            <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Project Information</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-[var(--text-secondary)]">Project Name</label>
                  <p className="text-[var(--text-primary)] font-medium">{project.name || 'Untitled Project'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--text-secondary)]">Project Type</label>
                  <p className="text-[var(--text-primary)] capitalize">{project.type || 'Unknown'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--text-secondary)]">Customer</label>
                  <p className="text-[var(--text-primary)]">{project.customer || 'Unknown Customer'}</p>
                </div>
                {project.customerAddress && (
                  <div>
                    <label className="text-sm font-medium text-[var(--text-secondary)]">Address</label>
                    <p className="text-[var(--text-primary)]">{project.customerAddress}</p>
                  </div>
                )}
                {project.customerPhone && (
                  <div>
                    <label className="text-sm font-medium text-[var(--text-secondary)]">Phone</label>
                    <p className="text-[var(--text-primary)]">{project.customerPhone}</p>
                  </div>
                )}
                {project.status && (
                  <div>
                    <label className="text-sm font-medium text-[var(--text-secondary)]">Status</label>
                    <p className="text-[var(--text-primary)] capitalize">{project.status}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Equipment Details */}
            {project.equipment && project.equipment.length > 0 && (
              <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4">
                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Equipment Details</h3>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {project.equipment.map((item, index) => (
                    <div key={index} className="border border-[var(--border-color)] rounded p-3 bg-[var(--bg-secondary)]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getEquipmentColor(item.type) }}></div>
                        <h4 className="font-medium text-[var(--text-primary)]">{formatEquipmentType(item.type)}</h4>
                      </div>
                      {item.notes && (
                        <p className="text-sm text-[var(--text-secondary)] mb-2">{item.notes}</p>
                      )}
                      {item.details && Object.keys(item.details).length > 0 && (
                        <div className="text-xs text-[var(--text-secondary)] mb-2">
                          {Object.entries(item.details).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="font-medium">{key}:</span>
                              <span>{value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Photo Section */}
                      {item.photos && item.photos.length > 0 && (
                        <div className="mt-3">
                          <h5 className="text-sm font-medium text-[var(--text-secondary)] mb-2">Photos ({item.photos.length})</h5>
                          <div className="grid grid-cols-2 gap-2">
                            {item.photos.map((photo, photoIndex) => (
                              <div key={photoIndex} className="relative group">
                                <img
                                  src={photo.url}
                                  alt={`${formatEquipmentType(item.type)} photo ${photoIndex + 1}`}
                                  className="w-full h-20 object-cover rounded border border-[var(--border-color)] cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => {
                                    setSelectedPhoto(photo);
                                    setShowPhotoViewer(true);
                                  }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded flex items-center justify-center">
                                  <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-medium">View</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conductor Details */}
            {project.conductors && project.conductors.length > 0 && (
              <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4">
                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Conductor Details</h3>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {project.conductors.map((conductor, index) => (
                    <div key={index} className="border border-[var(--border-color)] rounded p-3 bg-[var(--bg-secondary)]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-[var(--accent-primary)]"></div>
                        <h4 className="font-medium text-[var(--text-primary)]">{formatEquipmentType(conductor.type)}</h4>
                      </div>
                      <div className="text-sm text-[var(--text-secondary)] space-y-1">
                        {conductor.wireSize && (
                          <div className="flex justify-between">
                            <span className="font-medium">Wire Size:</span>
                            <span>{conductor.wireSize}</span>
                          </div>
                        )}
                        {conductor.length && (
                          <div className="flex justify-between">
                            <span className="font-medium">Length:</span>
                            <span>{parseFloat(conductor.length).toFixed(1)} ft</span>
                          </div>
                        )}
                        {conductor.notes && (
                          <div className="mt-2">
                            <span className="font-medium text-[var(--text-secondary)]">Notes:</span>
                            <p className="text-[var(--text-secondary)] mt-1">{conductor.notes}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Photo Section */}
                      {conductor.photos && conductor.photos.length > 0 && (
                        <div className="mt-3">
                          <h5 className="text-sm font-medium text-[var(--text-secondary)] mb-2">Photos ({conductor.photos.length})</h5>
                          <div className="grid grid-cols-2 gap-2">
                            {conductor.photos.map((photo, photoIndex) => (
                              <div key={photoIndex} className="relative group">
                                <img
                                  src={photo.url}
                                  alt={`${formatEquipmentType(conductor.type)} photo ${photoIndex + 1}`}
                                  className="w-full h-20 object-cover rounded border border-[var(--border-color)] cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => {
                                    setSelectedPhoto(photo);
                                    setShowPhotoViewer(true);
                                  }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded flex items-center justify-center">
                                  <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-medium">View</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Project Dates</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-[var(--text-secondary)]">Created</label>
                  <p className="text-[var(--text-primary)]">{formatDate(project.createdAt)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--text-secondary)]">Last Updated</label>
                  <p className="text-[var(--text-primary)]">{formatDate(project.updatedAt)}</p>
                </div>
              </div>
            </div>

            {/* Project Notes */}
            <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Project Notes</h3>
              <div className="text-sm text-[var(--text-secondary)]">
                {(() => {
                  const allNotes = [];
                  
                  // Add project-level notes
                  if (project.notes) {
                    allNotes.push({
                      type: 'project',
                      title: 'Project Notes',
                      notes: project.notes,
                      details: {}
                    });
                  }
                  
                  // Add equipment notes
                  project.equipment?.forEach((item, index) => {
                    if (item.notes) {
                      allNotes.push({
                        type: 'equipment',
                        title: `${formatEquipmentType(item.type)} ${index + 1}`,
                        notes: item.notes,
                        details: item.details || {}
                      });
                    }
                  });
                  
                  // Add conductor notes
                  project.conductors?.forEach((conductor, index) => {
                    if (conductor.notes) {
                      allNotes.push({
                        type: 'conductor',
                        title: `${formatEquipmentType(conductor.type)} ${index + 1}`,
                        notes: conductor.notes,
                        details: {
                          'Wire Size': conductor.wireSize,
                          'Length': `${parseFloat(conductor.length).toFixed(1)} ft`
                        }
                      });
                    }
                  });
                  
                  if (allNotes.length === 0) {
                    return (
                      <p className="text-[var(--text-secondary)] text-sm">
                        No notes have been added to this project yet.
                      </p>
                    );
                  }
                  
                  return (
                    <div className="space-y-3">
                      {allNotes.map((note, index) => (
                        <div key={index} className="border-l-4 border-[var(--accent-primary)] pl-3 py-2">
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-2 h-2 rounded-full ${
                              note.type === 'project' ? 'bg-purple-500' :
                              note.type === 'equipment' ? 'bg-green-500' : 'bg-[var(--accent-primary)]'
                            }`}></div>
                            <h4 className="font-medium text-[var(--text-primary)] text-sm">{note.title}</h4>
                          </div>
                          <p className="text-[var(--text-secondary)] text-sm mb-1">{note.notes}</p>
                          {Object.keys(note.details || {}).length > 0 && (
                            <div className="text-xs text-[var(--text-secondary)]">
                              {Object.entries(note.details).map(([key, value]) => (
                                <span key={key} className="inline-block mr-2">
                                  <strong>{key}:</strong> {value}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Project Photos Summary */}
            {(() => {
              const allPhotos = [];
              
              // Collect all photos from equipment
              project.equipment?.forEach(item => {
                if (item.photos) {
                  allPhotos.push(...item.photos);
                }
              });
              
              // Collect all photos from conductors
              project.conductors?.forEach(conductor => {
                if (conductor.photos) {
                  allPhotos.push(...conductor.photos);
                }
              });
              
              if (allPhotos.length === 0) {
                return null;
              }
              
              return (
                <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4">
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Project Photos ({allPhotos.length})</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {allPhotos.slice(0, 6).map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={photo.url}
                          alt={`Project photo ${index + 1}`}
                          className="w-full h-16 object-cover rounded border border-[var(--border-color)] cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setSelectedPhoto(photo);
                            setShowPhotoViewer(true);
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded flex items-center justify-center">
                          <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-medium">View</span>
                        </div>
                      </div>
                    ))}
                    {allPhotos.length > 6 && (
                      <div className="flex items-center justify-center h-16 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded text-[var(--text-secondary)] text-sm">
                        +{allPhotos.length - 6} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Photo Viewer Modal */}
      {showPhotoViewer && selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--bg-secondary)] rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-[var(--border-color)] flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Photo Viewer</h3>
              <button
                onClick={() => {
                  setShowPhotoViewer(false);
                  setSelectedPhoto(null);
                }}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <img
                src={selectedPhoto.url}
                alt="Full size photo"
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            </div>
          </div>
        </div>
      )}

      {/* PDF Export Modal */}
      {showPDFExport && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--bg-secondary)] rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Export PDF</h3>
            <p className="text-[var(--text-secondary)] mb-6">
              This will generate a PDF snapshot of the project map and details.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPDFExport(false);
                  // Add PDF export logic here
                }}
                className="flex-1 bg-[var(--accent-primary)] text-white py-2 px-4 rounded-lg hover:bg-[var(--accent-secondary)] transition-colors"
              >
                Export
              </button>
              <button
                onClick={() => setShowPDFExport(false)}
                className="flex-1 bg-[var(--bg-tertiary)] text-[var(--text-primary)] py-2 px-4 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors border border-[var(--border-color)]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails; 